import { List, Map, Set, Record, fromJS, OrderedMap } from 'immutable';

const matchRegexp = /^(\D+)(\d+)$/;

const ListItemFactory = new Record({
  id: '',
  value: '',
  display: '',
  isEditable: false,
  isActive: true,
  readonly: false,
  selected: false,
  group: null,
});

export const RecordCellSet = new Record({
  value: null,
  display: '',
  options: null,
});

function createPolygon() {
  return {
    x: '',
    y: '',
    h: '',
    w: '',
  };
}

const RecordBoundingPoly = new Record(createPolygon());

export const RecordPolyCellSet = new Record({
  value: '',
  entityType: '',
  boundingPoly: RecordBoundingPoly(),
});

export function matchCell(cell) {
  const res = cell.match(matchRegexp);
  if (res && res.length === 3) {
    return [res[1], parseInt(res[2], 10)];
  }
  throw new Error(`Cell not match pattern ${cell}`);
}

function styleFactory(style) {
  if (style) return fromJS(style);
  return new Map();
}

const StringCellFactory = new Record({
  _cell: '',
  _row: '',
  _col: '',
  return: true,
  name: '',
  readonly: false,
  type: 'string',
  bold: false,
  hint: null,
  link: null,
  lenght: null,
  maxLength: undefined,
  regex: null,
  valid: true,
  mandatory: false,
  focused: false,
  hidden: false,
  cellSet: RecordCellSet(),
  style: new Map(),
});

function StringCellAdapter({ set, style, lenght, ...rest }) {
  return StringCellFactory({
    ...rest,
    cellSet: RecordCellSet(set),
    style: styleFactory(style),
    lenght,
    maxLength: lenght,
  });
}

export const DescriptionCellFactory = new Record({
  _cell: '',
  _row: '',
  _col: '',
  headerID: '',
  return: false,
  bold: true,
  hint: null,
  text: '',
  link: null,
  readonly: true,
  type: 'description',
  name: '',
  regex: null,
  valid: true,
  mandatory: false,
  focused: false,
  hidden: false,
  style: new Map(),
});

function DescriptionCellAdapter({ text, style, ...rest }) {
  return DescriptionCellFactory({
    ...rest,
    text: (text || '').toString().trim(),
    style: styleFactory(style),
  });
}

const FloatCellFactory = new Record({
  _cell: '',
  _row: '',
  _col: '',
  return: true,
  name: '',
  readonly: false,
  type: 'float',
  bold: false,
  hint: null,
  regex: null,
  valid: true,
  mandatory: false,
  focused: false,
  hidden: false,
  cellSet: RecordCellSet(),
  style: new Map(),
});

function FloatCellAdapter({ set, style, ...rest }) {
  return FloatCellFactory({
    ...rest,
    cellSet: RecordCellSet(set),
    style: styleFactory(style),
  });
}

// Entries the item-search service scored as likely matches for the line. Any other group id is
// treated as no group, so a future second group cannot change how the current list renders.
export const SUGGESTED_GROUP = 'suggested';

export function listToOptions(list) {
  // `id` is assigned before the partition below, so it keeps meaning what it has always meant —
  // the entry's position in the backend response, not its position on screen.
  const options = Object.values(list).map(({ display, value, isEditable, isActive, readonly, group }, id) =>
    ListItemFactory({ id, display, value, isEditable, isActive, readonly, group }),
  );

  // Suggested entries lead the list. Two filters rather than a sort: the partition is obviously
  // stable, so the backend's best-match-first order survives inside each group.
  // The partition runs per response page, which is all the contract asks for — suggested entries
  // only arrive on the first page. Were they ever to come on a later page they would lead that
  // page's chunk instead of the whole list; partition in the reducer's page concat if that happens.
  return List([
    ...options.filter(({ group }) => group === SUGGESTED_GROUP),
    ...options.filter(({ group }) => group !== SUGGESTED_GROUP),
  ]);
}

// The divider sits under the last consecutive suggested entry, and only when an ordinary entry
// follows it — a line with nothing under it separates nothing.
/**
 * @param {import('immutable').List<any>} list options list, possibly shifted by an add-new slot
 * @param {number} index
 */
export function isSuggestedGroupBoundary(list, index) {
  const current = list.get(index);
  const next = list.get(index + 1);
  return !!current && !!next && current.group === SUGGESTED_GROUP && next.group !== SUGGESTED_GROUP;
}

const ListCellFactory = new Record({
  _cell: '',
  _row: '',
  _col: '',
  return: true,
  name: '',
  readonly: false,
  type: 'index_list',
  isStaticList: true,
  bold: false,
  hint: null,
  includes: '',
  _options: new List(),
  regex: null,
  valid: true,
  mandatory: false,
  focused: false,
  hidden: false,
  isCreatable: false,
  cellSet: RecordCellSet(),
  style: new Map(),
  pageToken: null,
  count: 0,
  include_id: null,
});

// eslint-disable-next-line max-len
function ListCellAdapter({ list, set, style, ...rest }) {
  return ListCellFactory({
    ...rest,
    isStaticList: rest.is_static_list,
    _options: listToOptions(list || []),
    cellSet: RecordCellSet(set),
    style: styleFactory(style),
  });
}

const StringGeneratedFactory = new Record({
  _cell: '',
  _row: '',
  _col: '',
  return: true,
  name: '',
  readonly: false,
  type: 'string_auto_generated',
  initValue: '',
  bold: false,
  hint: null,
  link: null,
  maxLength: null,
  regex: null,
  valid: true,
  mandatory: false,
  focused: false,
  hidden: false,
  cellSet: RecordCellSet(),
  style: new Map(),
});

// eslint-disable-next-line camelcase
function StringGeneratedAdapter({ set, style, init_value, lenght, ...rest }) {
  return StringGeneratedFactory({
    ...rest,
    initValue: init_value,
    maxLength: lenght,
    cellSet: RecordCellSet(set),
    style: styleFactory(style),
  });
}

const CellSetFactory = new Record({
  value: null,
});

const CheckBoxFactory = new Record({
  _cell: '',
  _row: '',
  _col: '',
  bold: false,
  hint: null,
  name: '',
  readonly: false,
  return: true,
  hidden: false,
  cellSet: CellSetFactory(),
  style: new Map(),
  type: 'checkbox',
});

function CheckBoxAdapter({ set, style, list, ...rest }) {
  return CheckBoxFactory({
    ...rest,
    cellSet: CellSetFactory(set),
    style: styleFactory(style),
  });
}

const ReconciliationFactory = new Record({
  _cell: '',
  _row: '',
  _col: '',
  type: 'reconciliation',
  bold: false,
  hint: null,
  name: '',
  readonly: false,
  return: true,
  hidden: false,
  cellSet: CellSetFactory(),
  style: new Map(),
});

// eslint-disable-next-line max-len
export function reconciliationAdapter({ set, style, list, ...rest }) {
  return ReconciliationFactory({
    ...rest,
    cellSet: CellSetFactory(set),
    style: styleFactory(style),
  });
}

function matchType(cell) {
  switch (cell.type) {
    case 'string':
      return StringCellAdapter(cell);

    case 'description':
      return DescriptionCellAdapter(cell);

    case 'float':
      return FloatCellAdapter(cell);

    case 'index_list':
      return ListCellAdapter(cell);

    case 'paginated_list':
    case 'paginated_multiselect_list':
      return ListCellAdapter(cell);

    case 'string_auto_generated':
      return StringGeneratedAdapter(cell);

    case 'checkbox':
      return CheckBoxAdapter(cell);

    case 'reconciliation':
      return reconciliationAdapter(cell);

    default:
      throw new Error('Unknown cell type');
  }
}

/**
 * parse cell name from A32 to alphabetic and digit
 * @param cellName - A32
 * @returns {[string,number]} - ['A', 32], [_col, _row]
 */
export const parseCellName = (cellName) => matchCell(cellName);

// eslint-disable-next-line max-len
export function getRowByNumber(data, rows) {
  return data.reduce((a, v, k) => (rows.includes(matchCell(k)[1]) ? a.set(k, v) : a), new OrderedMap());
}

export const MetaFactory = new Record({
  currentPage: 1,
  pageCount: 1,
  pageSize: 50,
});

export const PaginationListFactory = new Record({
  list: new List(),
  pageToken: null,
  isLoading: false,
  cachedList: new Map(),
});

export const JournalFactory = new Record({
  jeName: 'Journal Entry',
  messages: new List(),
  publicationWarnings: new Set(),
  entry: new OrderedMap(),
  includes: new Map(),
  paginationList: PaginationListFactory(),
  jeType: null,
  jeLayout: null,
  jeInvoiceType: null,
  jeCurrency: null,
  line_items: new Set(),
  settings: new Set(),
  lines: new Set(),
  rowMessages: new List(),
  summary: new Set(),
  initial_hidden_columns: new List(),
  notHiddenColumns: new Set(),
  meta: MetaFactory(),
  lineCount: 0,
  headerLinesNumber: 0,
  selectedRow: new Set(),
  reconcileColumn: '',
  pinnedColumn: new List(),
  amountColumn: '',
  transactionTypeField: null,
  transactionTypeColumn: '',
  dateColumn: '',
  descriptionColumn: '',
  transactionIdColumn: '',
  balanceColumn: '',
  errorFromErp: null,
  jeGridLineColumnsDictionary: new OrderedMap(),
  matchedLines: new Map(),
  loading: false,
  editing: false,
  textractForVendorEnabled: false,
  wasFixed: false,
});

export function isValid(cell) {
  if (
    typeof cell.value === 'undefined' ||
    cell.value === null ||
    cell.regex === null ||
    typeof cell.regex === 'undefined'
  )
    return true;
  return new RegExp(cell.regex, 'i').test(cell.value);
}

export function dataAdapter(data) {
  if (!data) throw new Error('No journal entry data');
  return Object.entries(data).reduce((a, [index, item]) => {
    const [_col, _row] = parseCellName(index);
    const inCall = { ...item, _cell: index, _row, _col, valid: isValid(item) };
    const cell = matchType(inCall);
    if (cell) return a.set(index, cell);
    return a;
  }, new OrderedMap());
}

export function jeGridLineColumnsAdapter(data) {
  if (!data) throw new Error('No journal entry data');
  return Object.entries(data).reduce((a, [index, item]) => {
    if (item.headerID) {
      const [_col] = parseCellName(index);

      return a.set(_col, item.headerID);
    }

    return a;
  }, new OrderedMap());
}

export function includeAdapter(include) {
  return Object.entries(include).reduce(
    (res, [fieldName, fieldData]) => res.set(fieldName, listToOptions(fieldData, true)),
    new Map(),
  );
}

export function includesAdapter(includes) {
  return Object.entries(includes).reduce((res, [name, item]) => res.set(name, includeAdapter(item)), new Map());
}

export const JournalSettingOrderFactory = new Record({
  pinned: [],
  float: [],
});

export const JournalSettingsFactory = new Record({
  pinnedCols: [],
  disabledCols: [],
  orderedCols: JournalSettingOrderFactory({}),
});

// ======

function getPolyGonMatches(areaCoordinates, polygonCoordinates) {
  const { aXStart, aXEnd, aYStart, aYEnd } = areaCoordinates;
  const { pXStart, pYStart, pXEnd, pYEnd } = polygonCoordinates;

  return {
    fullMatch: pXStart >= aXStart && pXEnd <= aXEnd && pYStart >= aYStart && pYEnd <= aYEnd,
    notMatch: aXEnd < pXStart || aYEnd < pYStart,
  };
}

function getMatchedCorner(areaCoordinates, polygonCoordinates) {
  const { aXStart, aXEnd, aYStart, aYEnd } = areaCoordinates;
  const { pXStart, pYStart, pXEnd, pYEnd } = polygonCoordinates;

  const leftTopCorner = aXStart < pXStart && aYStart < pYStart;
  const rightTopCorner = aYStart < pYStart && aXEnd > pXEnd;
  const leftBottomCorner = aYEnd > pYEnd && aXStart < pXStart;
  const rightBottomCorner = aXEnd > pXEnd && aYEnd > pYEnd;

  return {
    leftTopCorner,
    rightTopCorner,
    leftBottomCorner,
    rightBottomCorner,
  };
}

function polygonPartlyMatch(areaCoordinates, polygonCoordinates) {
  const { aXStart, aXEnd, aYStart, aYEnd } = areaCoordinates;
  const { pXStart, pYStart, pXEnd, pYEnd } = polygonCoordinates;

  const { leftTopCorner, rightTopCorner, leftBottomCorner, rightBottomCorner } = getMatchedCorner(
    areaCoordinates,
    polygonCoordinates,
  );

  const matchedWidth = (pXEnd - pXStart) * 0.5;
  const matchedHeight = (pYEnd - pYStart) * 0.5;

  if (leftTopCorner) {
    const xDiff = aXEnd - pXStart;
    const yDiff = aYEnd - pYStart;

    return xDiff > matchedWidth && yDiff > matchedHeight;
  }

  if (rightTopCorner) {
    const xDiff = pXEnd - aXStart;
    const yDiff = aYEnd - pYStart;

    return xDiff > matchedWidth && yDiff > matchedHeight;
  }

  if (leftBottomCorner) {
    const xDiff = aXEnd - pXStart;
    const yDiff = pYEnd - aYStart;

    return xDiff > matchedWidth && yDiff > matchedHeight;
  }

  if (rightBottomCorner) {
    const xDiff = pXEnd - aXStart;
    const yDiff = pYEnd - aYStart;

    return xDiff > matchedWidth && yDiff > matchedHeight;
  }

  return false;
}

export function isPolygonInArea(area, polygon) {
  const { width: aWidth, height: aHeight, x: aXStart, y: aYStart } = area;
  const { left: pXStart, top: pYStart, width: pWidth, height: pHeight } = polygon;

  const aXEnd = aXStart + aWidth;
  const aYEnd = aYStart + aHeight;
  const pXEnd = pXStart + pWidth;
  const pYEnd = pYStart + pHeight;

  const areaCoordinates = { aXStart, aXEnd, aYStart, aYEnd };
  const polygonCoordinates = { pXStart, pYStart, pXEnd, pYEnd };

  const { fullMatch, notMatch } = getPolyGonMatches(areaCoordinates, polygonCoordinates);

  if (notMatch) return false;
  if (fullMatch) return true;
  return polygonPartlyMatch(areaCoordinates, polygonCoordinates);
}

export const jeSorterCb = (a, b) =>
  // A1, A9, A10, B1, ... Z1, AA1, AB1
  a.get('_col').length - b.get('_col').length || a.get('_cell').localeCompare(b.get('_cell'), 'en', { numeric: true });

// v2 - used like marker for migration from letters column to ids in indexedDB
export const generateJETableStoreKey = (keys = [], withSuffix = true) => keys.concat(withSuffix ? 'v2' : []).join('-');

/**
 * In indexedDB for Journal Entry grid table we store column IDs, but we need column letters for work with data in react
 * this function can serialize and unserialize data from columnLetters to columnIDs and vica versa
 * @param data - this data from indexedDB or will be putted to IndexedDB: Array(['A', 'B', 'C']) - for tablePinnedCols, tableDisabledCols
 *               or Object({'A': 230, 'B': 120, 'C': 180 }) for tableColsWidth
 * @param dictionary - dictionary(OrderedMap) where stored relation columnLetter to columnID, default structure {columnLetter: columnID}, flipped - {columnID: columnLetter}
 * @param isFlippedDictionary - marker:  if true, we flip dictionary and can unserialize column ids to column letters(set to true when get data from indexedDB)
 * @returns {[string,*]|{}|*}
 */
export const swapJEGridLineColumnsData = (data, dictionary, isFlippedDictionary = false) => {
  const currentDictionary = isFlippedDictionary ? dictionary.flip() : dictionary;

  if (Array.isArray(data)) {
    return data.map((columnKey) => currentDictionary.get(columnKey));
  }

  return Object.entries(data).reduce((acc, [columnKey, columnValue]) => {
    const currentColumnKey = currentDictionary.get(columnKey);

    acc[currentColumnKey] = columnValue;

    return acc;
  }, {});
};


